import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Shield, Lock, Send, ShieldCheck, Building2, AlertCircle, Loader2 } from 'lucide-react';
import { ApiEndPoint } from '../services/apiUttiLities';

const fmt = (ts) => new Date(ts).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
const lsKey = (slug) => `partner_access_${slug}`;

export default function PartnerPage() {
  const { slug } = useParams();
  const [meta, setMeta] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [granted, setGranted] = useState(false);
  const [pageId, setPageId] = useState(null);
  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [gateError, setGateError] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState('');
  const scrollRef = useRef(null);

  // Load page meta
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${ApiEndPoint}/api/partner/${slug}`);
        const j = await r.json();
        if (cancelled) return;
        if (j?.status && j.data) setMeta(j.data); else setNotFound(true);
      } catch (e) { if (!cancelled) setNotFound(true); }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Auto-restore saved access for this device
  useEffect(() => {
    if (!meta) return;
    try {
      const saved = JSON.parse(localStorage.getItem(lsKey(slug)) || 'null');
      if (saved?.code && saved?.name) {
        setCode(saved.code); setName(saved.name);
        verify(saved.code, saved.name, true);
      }
    } catch (e) { /* noop */ }
  }, [meta]);

  const loadMessages = async (accessCode) => {
    try {
      const r = await fetch(`${ApiEndPoint}/api/partner/${slug}/messages?code=${encodeURIComponent(accessCode)}`);
      const j = await r.json();
      if (j?.status && j.data) { setMessages(j.data.messages || []); return j.data.page_id; }
    } catch (e) { /* noop */ }
    return null;
  };

  const verify = async (c = code, n = name, silent = false) => {
    if (!c.trim() || !n.trim()) { if (!silent) setGateError('Enter your name and access code'); return; }
    setVerifying(true); setGateError('');
    try {
      const r = await fetch(`${ApiEndPoint}/api/partner/${slug}/verify`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: c.trim(), name: n.trim() }),
      });
      const j = await r.json();
      if (j?.status && j.data) {
        localStorage.setItem(lsKey(slug), JSON.stringify({ code: c.trim(), name: n.trim() }));
        setPageId(j.data.page_id);
        setGranted(true);
        await loadMessages(c.trim());
      } else {
        setGateError(j?.message || 'Invalid access code');
        if (!silent) localStorage.removeItem(lsKey(slug));
      }
    } catch (e) { setGateError('Connection error. Please try again.'); }
    finally { setVerifying(false); }
  };

  // Poll for new messages (reliable through /api ingress)
  useEffect(() => {
    if (!granted) return;
    const id = setInterval(async () => {
      try {
        const r = await fetch(`${ApiEndPoint}/api/partner/${slug}/messages?code=${encodeURIComponent(code.trim())}`);
        const j = await r.json();
        if (j?.status && j.data && Array.isArray(j.data.messages)) setMessages(j.data.messages);
      } catch (e) { /* noop */ }
    }, 2500);
    return () => clearInterval(id);
  }, [granted, slug, code]);

  useEffect(() => { if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight; }, [messages.length]);

  const send = async () => {
    if (!msg.trim()) return;
    const text = msg.trim(); setMsg('');
    try {
      const r = await fetch(`${ApiEndPoint}/api/partner/${slug}/messages`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ access_code: code.trim(), author: name.trim(), text }),
      });
      const j = await r.json();
      if (j?.status && j.data) setMessages((prev) => (prev.some((m) => m.id === j.data.id) ? prev : [...prev, j.data]));
    } catch (e) { /* noop */ }
  };

  const headerTitle = useMemo(() => meta?.name || 'Secure Partner Channel', [meta]);

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white p-6">
        <div className="text-center">
          <AlertCircle className="h-12 w-12 mx-auto mb-3 text-rose-400" />
          <h1 className="text-xl font-bold">Page not found</h1>
          <p className="text-slate-400 text-sm mt-1">This secure partner link is invalid or has been removed.</p>
        </div>
      </div>
    );
  }

  if (!meta) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-900 text-white"><Loader2 className="h-6 w-6 animate-spin" /></div>;
  }

  // Access gate
  if (!granted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-900 to-indigo-950 p-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden" data-testid="partner-gate">
          <div className="bg-slate-900 text-white px-6 py-5">
            <div className="flex items-center gap-2 mb-1"><Shield className="h-5 w-5 text-indigo-300" /><span className="text-xs font-bold uppercase tracking-wider text-indigo-300">Secure Partner Channel</span></div>
            <h1 className="text-xl font-bold">{headerTitle}</h1>
            {meta.org && <p className="text-[12px] text-slate-400 flex items-center gap-1 mt-1"><Building2 className="h-3.5 w-3.5" /> {meta.org}</p>}
          </div>
          <div className="p-6 space-y-4">
            <p className="text-[13px] text-gray-500">Enter your name and the access code shared with you to join this encrypted coordination channel.</p>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400">Your name *</label>
              <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Officer Khalid" data-testid="partner-gate-name"
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2.5 mt-1 focus:outline-none focus:border-indigo-400" />
            </div>
            <div>
              <label className="text-[10px] font-bold uppercase tracking-wider text-gray-400 flex items-center gap-1"><Lock className="h-3 w-3" /> Access code *</label>
              <input value={code} onChange={(e) => setCode(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && verify()} placeholder="e.g. CD-7842" data-testid="partner-gate-code"
                className="w-full text-sm border border-gray-300 rounded-md px-3 py-2.5 mt-1 focus:outline-none focus:border-indigo-400" />
            </div>
            {gateError && <p className="text-xs text-rose-600 flex items-center gap-1" data-testid="partner-gate-error"><AlertCircle className="h-3.5 w-3.5" /> {gateError}</p>}
            <button onClick={() => verify()} disabled={verifying} data-testid="partner-gate-submit"
              className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-md py-2.5 text-sm font-bold flex items-center justify-center gap-2">
              {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />} Enter secure channel
            </button>
            <p className="text-[10px] text-gray-400 text-center flex items-center justify-center gap-1"><Lock className="h-3 w-3" /> No account required · access is scoped to this page only</p>
          </div>
        </div>
      </div>
    );
  }

  // Chat
  return (
    <div className="min-h-screen flex flex-col bg-slate-100">
      <div className="bg-slate-900 text-white px-5 py-3 flex items-center justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2"><Shield className="h-4 w-4 text-indigo-300" /><h1 className="font-bold text-[15px] truncate">{headerTitle}</h1></div>
          {meta.org && <p className="text-[11px] text-slate-400 truncate">{meta.org}</p>}
        </div>
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 px-2.5 py-1 text-[10px] font-bold"><ShieldCheck className="h-3.5 w-3.5" /> Secured · {name}</span>
      </div>
      <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-3 max-w-3xl w-full mx-auto" data-testid="partner-page-messages">
        {messages.length === 0 && (
          <div className="text-center text-gray-400 text-sm py-10"><Shield className="h-8 w-8 mx-auto mb-2 text-gray-300" />Secure channel established. Start the conversation.</div>
        )}
        {messages.map((m) => {
          const mine = m.fromPartner && m.author === name;
          return (
            <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[78%] rounded-2xl px-4 py-2.5 ${mine ? 'bg-indigo-600 text-white' : (m.fromPartner ? 'bg-white border border-gray-200 text-gray-800' : 'bg-slate-800 text-white')}`}>
                <p className="text-[10px] font-bold opacity-70 mb-0.5">{m.author}{m.org ? ` · ${m.org}` : ''}</p>
                <p className="text-sm leading-relaxed">{m.text}</p>
                <p className={`text-[10px] mt-1 ${mine ? 'text-indigo-200' : 'opacity-50'}`}>{fmt(m.ts)}</p>
              </div>
            </div>
          );
        })}
      </div>
      <div className="bg-white border-t border-gray-200 p-3">
        <div className="max-w-3xl mx-auto flex items-center gap-2">
          <input value={msg} onChange={(e) => setMsg(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Type a secure message…" data-testid="partner-page-input"
            className="flex-1 text-sm border border-gray-300 rounded-full px-4 py-2.5 focus:outline-none focus:border-indigo-400" />
          <button onClick={send} disabled={!msg.trim()} data-testid="partner-page-send"
            className="flex items-center justify-center h-10 w-10 rounded-full bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white"><Send className="h-4 w-4" /></button>
        </div>
      </div>
    </div>
  );
}
